import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getUiText } from '../../lib/uiText';
import iconCerrar from '../../assets/icons/icon_cerrar.svg';
import iconFlechaMaximizar from '../../assets/icons/icon_flecha_maximizar.svg';
import styles from './Floorplan.module.css';

const FLOORPLAN_FADE_DURATION = 220;
const MODAL_FADE_DURATION = 240;

const getMobileScaleValue = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : null;
};

const Floorplan = ({ data }) => {
  const { locale } = useGlobal();
  const sectionRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [isImageVisible, setIsImageVisible] = useState(true);
  const [isModalMounted, setIsModalMounted] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const title = data?.titulo || '';
  const backgroundImageMedia = getStrapiMediaAttributes(data?.backgroundImage, {
    altFallback: title,
    titleFallback: title,
  });
  const backgroundImage = backgroundImageMedia.src;
  const hasBackgroundImage = Boolean(backgroundImage);
  const galleryItems = useMemo(
    () => (data?.galeria || []).filter((item) => item?.image?.url),
    [data?.galeria]
  );
  const totalItems = galleryItems.length;
  const activeItem = galleryItems[displayIndex] || null;
  const activeImageMedia = getStrapiMediaAttributes(activeItem?.image, {
    altFallback: activeItem?.nombreBoton || title || getUiText(locale, 'floorplan'),
    titleFallback: activeItem?.nombreBoton || title || getUiText(locale, 'floorplan'),
  });
  const activeImage = activeImageMedia.src;
  const mobileScale = getMobileScaleValue(activeItem?.scaleMovil);
  const modalStyle = mobileScale ? { '--floorplan-mobile-scale': mobileScale } : undefined;
  const floorplanStyle = hasBackgroundImage
    ? {
      backgroundImage: `url(${backgroundImage})`,
      ...(mobileScale ? { '--floorplan-mobile-scale': mobileScale } : {}),
    }
    : (mobileScale ? { '--floorplan-mobile-scale': mobileScale } : undefined);

  useEffect(() => {
    if (totalItems === 0) return undefined;
    if (currentIndex < totalItems && displayIndex < totalItems) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      if (currentIndex >= totalItems) {
        setCurrentIndex(0);
      }

      if (displayIndex >= totalItems) {
        setDisplayIndex(0);
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [currentIndex, displayIndex, totalItems]);

  useEffect(() => {
    if (!hasBackgroundImage) return;

    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let frameId = null;

    const updateParallax = () => {
      frameId = null;
      const rect = sectionEl.getBoundingClientRect();
      const viewHeight = window.innerHeight || 0;
      const progress = Math.min(Math.max((viewHeight - rect.top) / (viewHeight + rect.height), 0), 1);
      const offset = Math.round((progress - 0.5) * 200);
      const overlayOpacity = (0.3 + progress * 0.1).toFixed(3);
      sectionEl.style.setProperty('--floorplan-bg-y', `${offset}px`);
      sectionEl.style.setProperty('--floorplan-overlay-opacity', overlayOpacity);
    };

    const onScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(updateParallax);
    };

    updateParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [hasBackgroundImage]);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const titleEl = sectionEl.querySelector(`.${styles.floorplan__title}`);
    const galleryShellEl = sectionEl.querySelector(`.${styles.floorplan__galleryShell}`);
    const controlsEl = sectionEl.querySelector(`.${styles.floorplan__controls}`);
    const buttonEls = sectionEl.querySelectorAll(`.${styles.floorplan__button}`);
    const expandButtonEl = sectionEl.querySelector(`.${styles.floorplan__expandButton}`);

    gsap.set(sectionEl, { opacity: 0, y: 50 });
    if (titleEl) gsap.set(titleEl, { opacity: 0, y: 20 });
    if (galleryShellEl) gsap.set(galleryShellEl, { opacity: 0, y: 30, scale: 0.985 });
    if (controlsEl) gsap.set(controlsEl, { opacity: 0, y: 20 });
    if (buttonEls.length) gsap.set(buttonEls, { opacity: 0, y: 16 });
    if (expandButtonEl) gsap.set(expandButtonEl, { opacity: 0, y: 16, scale: 0.96 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;

          const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

          timeline.to(sectionEl, { opacity: 1, y: 0, duration: 0.6 });

          if (titleEl) {
            timeline.to(titleEl, { opacity: 1, y: 0, duration: 0.5 }, '-=0.25');
          }

          if (galleryShellEl) {
            timeline.to(galleryShellEl, { opacity: 1, y: 0, scale: 1, duration: 0.6 }, '-=0.2');
          }

          if (controlsEl) {
            timeline.to(controlsEl, { opacity: 1, y: 0, duration: 0.4 }, '-=0.28');
          }

          if (buttonEls.length) {
            timeline.to(buttonEls, { opacity: 1, y: 0, duration: 0.4, stagger: 0.08 }, '-=0.25');
          }

          if (expandButtonEl) {
            timeline.to(expandButtonEl, { opacity: 1, y: 0, scale: 1, duration: 0.35 }, '-=0.28');
          }

          observer.disconnect();
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, [totalItems]);

  useEffect(() => {
    if (currentIndex === displayIndex) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      const frameId = window.requestAnimationFrame(() => {
        setDisplayIndex(currentIndex);
        setIsImageVisible(true);
      });

      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    const hideFrameId = window.requestAnimationFrame(() => {
      setIsImageVisible(false);
    });

    const swapTimeout = window.setTimeout(() => {
      setDisplayIndex(currentIndex);
      window.requestAnimationFrame(() => {
        setIsImageVisible(true);
      });
    }, FLOORPLAN_FADE_DURATION);

    return () => {
      window.cancelAnimationFrame(hideFrameId);
      window.clearTimeout(swapTimeout);
    };
  }, [currentIndex, displayIndex]);

  useEffect(() => {
    if (!isModalMounted || isModalVisible) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      const frameId = window.requestAnimationFrame(() => {
        setIsModalMounted(false);
      });

      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    const closeTimeout = window.setTimeout(() => {
      setIsModalMounted(false);
    }, MODAL_FADE_DURATION);

    return () => {
      window.clearTimeout(closeTimeout);
    };
  }, [isModalMounted, isModalVisible]);

  useEffect(() => {
    if (!isModalMounted) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsModalVisible(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isModalMounted]);

  if (!title && !hasBackgroundImage && totalItems === 0) return null;

  const hasGalleryNavigation = totalItems > 1;

  const handleFloorplanChange = (index) => {
    if (index === currentIndex) return;
    setCurrentIndex(index);
  };

  const openExpandedModal = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    setIsModalMounted(true);

    if (prefersReducedMotion) {
      setIsModalVisible(true);
      return;
    }

    window.requestAnimationFrame(() => {
      setIsModalVisible(true);
    });
  };

  const closeExpandedModal = () => {
    setIsModalVisible(false);
  };

  return (
    <section
      ref={sectionRef}
      className={`${styles.floorplan} ${hasBackgroundImage ? styles['floorplan--image'] : styles['floorplan--plain']}`}
      style={floorplanStyle}
      title={hasBackgroundImage ? backgroundImageMedia.title : undefined}
    >
      {hasBackgroundImage && <div className={styles.floorplan__overlay} />}
      <div className={styles.floorplan__container}>
        {title && <h2 className={styles.floorplan__title}>{title}</h2>}
        {activeImage && (
          <div className={styles.floorplan__galleryShell}>
            <div className={styles.floorplan__galleryContent}>
              <div className={styles.floorplan__imageWrap}>
                <img
                  src={activeImage}
                  alt={activeImageMedia.alt}
                  title={activeImageMedia.title}
                  className={`${styles.floorplan__image} ${isImageVisible ? styles['floorplan__image--visible'] : styles['floorplan__image--hidden']}`}
                />
              </div>

              <div className={styles.floorplan__controls}>
                {hasGalleryNavigation && (
                  <div className={styles.floorplan__buttons}>
                    {galleryItems.map((item, index) => (
                      <button
                        key={`${item.id || item.documentId || item.nombreBoton || 'floorplan'}-${index}`}
                        type="button"
                        className={`${styles.floorplan__button} ${index === currentIndex ? styles['floorplan__button--active'] : ''}`}
                        onClick={() => handleFloorplanChange(index)}
                      >
                        {item.nombreBoton || getUiText(locale, 'imageLabel', { index: index + 1 })}
                      </button>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  className={styles.floorplan__expandButton}
                  aria-label={getUiText(locale, 'expandFloorplan')}
                  onClick={openExpandedModal}
                >
                  <img
                    src={iconFlechaMaximizar}
                    alt={getUiText(locale, 'expandFloorplan')}
                    title={getUiText(locale, 'expandFloorplan')}
                    aria-hidden="true"
                    className={styles.floorplan__expandIcon}
                  />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isModalMounted && activeImage && createPortal(
        <div
          className={`${styles.floorplan__modal} ${isModalVisible ? styles['floorplan__modal--visible'] : ''}`}
          style={modalStyle}
          role="dialog"
          aria-modal="true"
          aria-label={activeItem?.nombreBoton || title || getUiText(locale, 'expandedFloorplan')}
          onClick={closeExpandedModal}
        >
          <div className={styles.floorplan__modalInner} onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className={styles.floorplan__modalClose}
              aria-label={getUiText(locale, 'closeExpandedFloorplan')}
              onClick={closeExpandedModal}
            >
              <img src={iconCerrar} alt={getUiText(locale, 'closeExpandedFloorplan')} title={getUiText(locale, 'closeExpandedFloorplan')} aria-hidden="true" className={styles.floorplan__modalCloseIcon} />
            </button>

            <img
              src={activeImage}
              alt={activeImageMedia.alt}
              title={activeImageMedia.title}
              className={styles.floorplan__modalImage}
            />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default Floorplan;